import type { ErrorContext } from './types';

export type HydrationErrorKind = 'html' | 'text';

// React reports hydration mismatches through `onRecoverableError`. The wording differs
// between the dev build, the minified build and the two mismatch kinds, so match on all
// the forms we see in Sentry rather than on one message.
const HTML_MISMATCH_PATTERNS = [
  /hydration failed/i,
  /server rendered HTML didn't match/i,
  /expected server HTML to contain/i,
  /minified react error #418/i,
  /error-decoder\.html\?invariant=418/i,
  /react\.dev\/errors\/418/i,
];

const TEXT_MISMATCH_PATTERNS = [
  /text content does not match/i,
  /text content did not match/i,
  /minified react error #(?:423|425)/i,
  /error-decoder\.html\?invariant=(?:423|425)/i,
  /react\.dev\/errors\/(?:423|425)/i,
];

// The element names React prints for host components. Component names sit in the same
// stack but are minified in production and change from release to release, so only these
// are used for grouping.
const HTML_TAGS = new Set([
  'a',
  'abbr',
  'address',
  'article',
  'aside',
  'b',
  'blockquote',
  'body',
  'br',
  'button',
  'canvas',
  'caption',
  'cite',
  'code',
  'col',
  'colgroup',
  'dd',
  'details',
  'dialog',
  'div',
  'dl',
  'dt',
  'em',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'header',
  'hr',
  'html',
  'i',
  'iframe',
  'img',
  'input',
  'label',
  'legend',
  'li',
  'main',
  'map',
  'mark',
  'menu',
  'meta',
  'nav',
  'noscript',
  'ol',
  'optgroup',
  'option',
  'output',
  'p',
  'picture',
  'pre',
  'progress',
  'q',
  's',
  'script',
  'section',
  'select',
  'small',
  'source',
  'span',
  'strong',
  'style',
  'sub',
  'summary',
  'sup',
  'svg',
  'table',
  'tbody',
  'td',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'title',
  'tr',
  'u',
  'ul',
  'video',
]);

// How many host elements from the top of the stack identify the mismatch site. Four is
// enough to tell `p > p` inside a banner from `p > p` inside a carousel heading.
const HOST_CHAIN_LENGTH = 4;

const FRAME_NAME_PATTERNS = [
  /^\s*at\s+([^\s(]+)/, // Chrome, Firefox: "    at p (<anonymous>)"
  /^\s*([^@\s]+)@/, // Safari: "p@unknown:0:0"
];

const getFrameName = (line: string): string | undefined =>
  FRAME_NAME_PATTERNS.reduce<string | undefined>(
    (found, pattern) => found ?? line.match(pattern)?.[1],
    undefined
  );

const matchesAny = (message: string, patterns: RegExp[]): boolean =>
  patterns.some((pattern) => pattern.test(message));

export const getHydrationErrorKind = (message: string): HydrationErrorKind | undefined => {
  if (matchesAny(message, TEXT_MISMATCH_PATTERNS)) return 'text';
  if (matchesAny(message, HTML_MISMATCH_PATTERNS)) return 'html';
  return undefined;
};

/**
 * The innermost host elements of a React component stack, e.g. `p > p > div > div`.
 */
export const getHostElementChain = (componentStack?: string): string =>
  (componentStack ?? '')
    .split('\n')
    .map(getFrameName)
    .filter((name): name is string => !!name && HTML_TAGS.has(name))
    .slice(0, HOST_CHAIN_LENGTH)
    .join(' > ');

/**
 * Groups hydration mismatches by where they happen instead of by message.
 *
 * React gives every mismatch the same handful of messages, so fingerprinting on the
 * message collapses every hydration bug on the site into one Sentry issue. `uiComponentName`
 * is a tag set per page by ObservabilityMetadata; Sentry substitutes it when grouping.
 *
 * Returns undefined for anything that isn't a hydration mismatch.
 */
export const getHydrationFingerprint = (
  error: Error,
  errorContext?: ErrorContext
): string[] | undefined => {
  if (!errorContext?.isRecoverableError) return undefined;

  const kind = getHydrationErrorKind(error.message ?? '');
  if (!kind) return undefined;

  return [
    'hydration-mismatch',
    kind,
    '{{ tags.uiComponentName }}',
    getHostElementChain(errorContext.errorInfo?.componentStack),
  ];
};
